// RELY - Corporate Sidebar (shadcn/ui)
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  X,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  ChevronUp,
  User,
} from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { ROUTES } from '../../constants/routes';
import { useCompany } from 'contexts/CompanyContext';
import {
  getRelyNavigationIcon,
  getRelyNavigationIconName,
} from 'components/navigation/relyNavigationIcons';
import { getClientNavigationItems } from 'navigation/clientRouteManifest';
import { hasRelyCommandAccess } from 'components/billing/relyCommandAccess';
import { useBilling } from 'hooks/useBilling';
import CompanySelector from './components/CompanySelector';
import SidebarSection from './components/SidebarSection';
import CommandFeatureLink from './CommandFeatureLink';
import AccountThemeSwitcher from './components/AccountThemeSwitcher';
import { isRelyCommandPreviewEnabled } from 'config/relyCommandPreview';

// Navigation link component
function SidebarLink({
  to,
  icon: Icon,
  label,
  iconKey,
  iconName,
  collapsed,
  isActive,
  disabled,
  badge,
}) {
  const { t } = useTranslation();
  const state = disabled ? 'disabled' : isActive ? 'active' : 'inactive';
  const content = (
    <div
      data-density="compact"
      data-nav-item={iconKey}
      data-state={state}
      className={cn(
        'group relative flex items-center rounded-[var(--rely-radius-surface)] border',
        'transition-colors duration-200',
        disabled
          ? 'cursor-not-allowed border-transparent opacity-50'
          : isActive
          ? 'border-transparent bg-[var(--rely-surface-selected)] text-[var(--rely-text-strong)]'
          : 'border-transparent text-[var(--rely-text-muted)] hover:bg-[var(--rely-surface-elevated)] hover:text-[var(--rely-text-strong)]',
        collapsed && 'mx-auto w-11'
      )}
    >
      <div
        className={cn(
          'flex w-full cursor-pointer items-center',
          collapsed ? 'justify-center py-2.5' : 'px-3 py-2.5'
        )}
      >
        {/* Icon */}
        <span
          data-nav-icon={iconKey}
          data-icon-name={iconName}
          data-icon-shell="navigation"
          data-slot="nav-icon-shell"
          className={cn(
            'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px]',
            'transition-[background-color,color] duration-200',
            isActive
              ? 'bg-[var(--rely-color-brand-green)]/10 text-[var(--rely-color-brand-green)]'
              : 'bg-transparent text-[var(--rely-text-muted)] group-hover:bg-[var(--rely-surface-soft)] group-hover:text-[var(--rely-text-strong)]'
          )}
          title={collapsed ? label : undefined}
        >
          <Icon className="h-4 w-4" strokeWidth={2.35} aria-hidden="true" />
        </span>

        {/* Label */}
        {!collapsed && (
          <span
            className={cn(
              'ml-3 text-sm leading-none',
              isActive
                ? 'font-semibold text-[var(--rely-text-strong)]'
                : 'font-medium text-[var(--rely-text-muted)] group-hover:text-[var(--rely-text-strong)]'
            )}
          >
            {label}
          </span>
        )}

        {/* Badge */}
        {badge && !collapsed && (
          <span
            className={cn(
              'ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold',
              badge === 'soon'
                ? 'bg-[var(--rely-surface-elevated)] text-[var(--rely-text-muted)]'
                : 'bg-[var(--rely-color-brand-green)] text-[var(--rely-color-brand-black)]'
            )}
          >
            {badge === 'soon' ? t('sidebar.comingSoon') : badge}
          </span>
        )}
      </div>
    </div>
  );

  if (disabled) {
    return (
      <div className="cursor-not-allowed" aria-disabled="true">
        {content}
      </div>
    );
  }

  return (
    <Link
      to={to}
      aria-current={isActive ? 'page' : undefined}
      aria-label={collapsed ? label : undefined}
      className={cn(
        'block rounded-[var(--rely-radius-surface)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--rely-surface-panel)]'
      )}
    >
      {content}
    </Link>
  );
}

const Sidebar = ({ open, onClose, collapsed, onToggleCollapse, variant }) => {
  const { user, logout } = useAuth0();
  const { t } = useTranslation();
  const location = useLocation();
  const { selectedCompanyId } = useCompany();
  const { subscription, hasRecurringSubscription } = useBilling();
  const navigationEntitlements = hasRelyCommandAccess({
    subscription,
    hasRecurringSubscription,
  })
    ? ['invoicing_membership']
    : [];
  const navigationFeatures = isRelyCommandPreviewEnabled()
    ? ['rely_command_preview']
    : [];
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const returnTo =
    window.location.hostname === 'localhost'
      ? 'http://localhost:3000/'
      : 'https://rely.business';

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logout({ logoutParams: { returnTo } });
  };

  const isRouteActive = (path) => {
    const normalizedPath = path.endsWith('/') ? path.slice(0, -1) : path;
    return (
      location.pathname === normalizedPath ||
      location.pathname.startsWith(`${normalizedPath}/`)
    );
  };

  const navIcon = (key) => ({
    icon: getRelyNavigationIcon(key),
    iconName: getRelyNavigationIconName(key),
  });

  const toSidebarRoute = (route) => ({
    to: route.href || ROUTES.HOME,
    ...navIcon(route.iconKey),
    iconKey: route.navigationKey || route.iconKey,
    label: t(route.labelKey, route.fallbackName),
    path: route.id,
    disabled: route.disabled,
  });
  const desktopRoutes = (section) =>
    getClientNavigationItems({
      placement: 'desktop',
      section,
      companyId: selectedCompanyId,
      entitlements: navigationEntitlements,
      features: navigationFeatures,
    }).map(toSidebarRoute);
  const primaryRoutes = desktopRoutes('primary');
  const financeRoutes = desktopRoutes('finance');
  const resourceRoutes = desktopRoutes('resources');
  const accountRoutes = desktopRoutes('account');

  return (
    <div
      className={cn(
        'fixed !z-50 min-h-full transition-transform duration-300 ease-in-out',
        'md:!z-50 lg:!z-50 xl:!z-0',
        variant === 'auth' ? 'xl:hidden' : 'xl:block',
        open ? '' : '-translate-x-full xl:translate-x-0'
      )}
    >
      {/* Sidebar Card */}
      <div
        data-slot="sidebar-shell"
        className={cn(
          'relative mt-4 h-[calc(100vh-2rem)]',
          'rounded-[var(--rely-radius-panel)]',
          'border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] shadow-[var(--rely-shadow-panel)] backdrop-blur-xl',
          collapsed ? 'ml-3 w-[72px]' : 'ml-4 w-[280px]'
        )}
      >
        {/* Close button - Mobile only (outside scroll container) */}
        <button
          className={cn(
            'absolute right-3 top-3 z-10 rounded-2xl p-2',
            'text-[var(--rely-text-muted)] hover:text-[var(--rely-text-strong)]',
            'transition-colors hover:bg-[var(--rely-surface-elevated)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            'xl:hidden'
          )}
          onClick={onClose}
          aria-label={t('sidebar.closeSidebar')}
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>

        {/* Collapse toggle button - Desktop only (outside scroll container) */}
        <button
          className={cn(
            'absolute z-10 rounded-2xl p-1.5',
            'text-[var(--rely-text-muted)] hover:text-[var(--rely-text-strong)]',
            'border border-[var(--rely-border-subtle)] hover:border-[var(--rely-color-brand-green)]',
            'bg-[var(--rely-surface-elevated)] shadow-sm transition-colors duration-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            'hidden items-center justify-center xl:flex',
            collapsed ? 'left-1/2 top-4 -translate-x-1/2' : 'right-4 top-4'
          )}
          onClick={onToggleCollapse}
          title={
            collapsed
              ? t('sidebar.expandSidebar')
              : t('sidebar.collapseSidebar')
          }
          aria-label={
            collapsed
              ? t('sidebar.expandSidebar')
              : t('sidebar.collapseSidebar')
          }
        >
          {collapsed ? (
            <PanelLeft className="h-4 w-4" aria-hidden="true" />
          ) : (
            <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
          )}
        </button>

        <div className="sidebar-scrollbar h-full overflow-y-auto overflow-x-hidden">
          <div className="flex h-full min-h-full flex-col justify-between py-5">
            <div>
              {/* Logo */}
              <Link
                to={ROUTES.HOME}
                className={cn(
                  'flex items-center justify-center rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  collapsed ? 'mb-4 mt-14 px-2' : 'mb-5 mt-10 px-6'
                )}
              >
                {collapsed ? (
                  <img
                    src="/favicon-32x32.png"
                    alt="Rely"
                    className="h-7 w-7"
                  />
                ) : (
                  <img
                    src="/rely_transparente.png"
                    alt="Rely Logo"
                    className="h-10 w-auto invert dark:invert-0"
                  />
                )}
              </Link>

              {/* Company Selector */}
              <div className="mb-4">
                <CompanySelector collapsed={collapsed} />
              </div>

              <nav
                className={cn(collapsed ? 'px-2' : 'px-3')}
                aria-label={t('navigation.mainNavigation')}
              >
                {selectedCompanyId && primaryRoutes.length > 0 && (
                  <ul
                    data-testid="sidebar-primary-nav"
                    className={cn(collapsed ? 'space-y-1' : 'space-y-1')}
                  >
                    {primaryRoutes.map((route) => (
                      <li key={route.path}>
                        {route.path === 'company_command' ? (
                          <CommandFeatureLink
                            to={route.to}
                            label={route.label}
                            collapsed={collapsed}
                            isActive={isRouteActive(route.to)}
                          />
                        ) : (
                          <SidebarLink
                            to={route.to}
                            icon={route.icon}
                            iconKey={route.iconKey}
                            iconName={route.iconName}
                            label={route.label}
                            collapsed={collapsed}
                            isActive={isRouteActive(route.to)}
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                <SidebarSection
                  title={t('navigation.finance')}
                  collapsed={collapsed}
                  className="mt-6"
                >
                  <ul className={cn(collapsed ? 'space-y-1' : 'space-y-0.5')}>
                    {financeRoutes.map((route) => (
                      <li key={route.path}>
                        <SidebarLink
                          to={route.to}
                          icon={route.icon}
                          iconKey={route.iconKey}
                          iconName={route.iconName}
                          label={route.label}
                          collapsed={collapsed}
                          isActive={isRouteActive(route.to)}
                          disabled={route.disabled}
                          badge={route.badge}
                        />
                      </li>
                    ))}
                  </ul>
                </SidebarSection>

                {resourceRoutes.length > 0 && (
                  <SidebarSection
                    title={t('navigation.resources')}
                    collapsed={collapsed}
                  >
                    <ul className={cn(collapsed ? 'space-y-1' : 'space-y-0.5')}>
                      {resourceRoutes.map((route) => (
                        <li key={route.path}>
                          <SidebarLink
                            to={route.to}
                            icon={route.icon}
                            iconKey={route.iconKey}
                            iconName={route.iconName}
                            label={route.label}
                            collapsed={collapsed}
                            isActive={isRouteActive(route.to)}
                          />
                        </li>
                      ))}
                    </ul>
                  </SidebarSection>
                )}
              </nav>
            </div>

            {/* User Section - Desktop only */}
            {user && (
              <div
                className={cn(
                  'relative border-t border-[var(--rely-border-subtle)]',
                  collapsed ? 'mx-2 px-1 py-3' : 'mx-3 px-2 py-4'
                )}
              >
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  aria-label={t('userMenu', 'User menu')}
                  aria-haspopup="menu"
                  aria-expanded={showUserMenu}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-2xl p-2',
                    'text-[var(--rely-text-muted)] hover:bg-[var(--rely-surface-elevated)] hover:text-[var(--rely-text-strong)]',
                    'transition-colors duration-200',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                    collapsed && 'justify-center'
                  )}
                >
                  {/* Avatar */}
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="h-9 w-9 flex-shrink-0 rounded-2xl object-cover"
                    />
                  ) : (
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl bg-[var(--rely-color-brand-green)] text-[var(--rely-color-brand-black)]">
                      <User className="h-5 w-5" aria-hidden="true" />
                    </div>
                  )}

                  {/* User info - only when not collapsed */}
                  {!collapsed && (
                    <>
                      <div className="flex-1 overflow-hidden text-left">
                        <p className="truncate text-sm font-medium text-[var(--rely-text-strong)]">
                          {user.name || user.email?.split('@')[0]}
                        </p>
                        <p className="truncate text-xs text-[var(--rely-text-muted)]">
                          {user.email}
                        </p>
                      </div>
                      <ChevronUp
                        className={cn(
                          'h-4 w-4 text-[var(--rely-text-muted)] transition-transform duration-200',
                          showUserMenu ? 'rotate-180' : ''
                        )}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div
                    role="menu"
                    className={cn(
                      'absolute bottom-full left-0 right-0 mb-2 rounded-2xl',
                      'border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] shadow-[var(--rely-shadow-panel)]',
                      collapsed ? 'left-1/2 w-48 -translate-x-1/2' : ''
                    )}
                  >
                    <div className="p-1.5">
                      {accountRoutes.map((route) => {
                        const AccountIcon = route.icon;
                        return (
                          <Link
                            key={route.path}
                            to={route.to}
                            role="menuitem"
                            onClick={() => setShowUserMenu(false)}
                            className="flex min-h-11 items-center gap-3 rounded-[var(--rely-radius-surface)] px-3 text-sm font-medium text-[var(--rely-text-muted)] transition-colors hover:bg-[var(--rely-surface-elevated)] hover:text-[var(--rely-text-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                          >
                            <AccountIcon
                              className="h-4 w-4"
                              aria-hidden="true"
                            />
                            {route.label}
                          </Link>
                        );
                      })}
                      <AccountThemeSwitcher />
                    </div>
                    <button
                      role="menuitem"
                      onClick={() => {
                        setShowUserMenu(false);
                        setShowLogoutConfirm(true);
                      }}
                      className={cn(
                        'flex w-full items-center gap-3 border-t border-[var(--rely-border-subtle)] px-4 py-3',
                        'text-error-600 hover:bg-error-500/10 dark:text-error-300',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error-500',
                        'transition-colors duration-200'
                      )}
                    >
                      <LogOut className="h-4 w-4" aria-hidden="true" />
                      <span className="text-sm font-medium">
                        {t('sidebar.logout')}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal - Rendered via Portal for proper z-index */}
      {showLogoutConfirm &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="sidebar-logout-dialog-title"
            aria-describedby="sidebar-logout-dialog-description"
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-neutral-950/55 p-4 backdrop-blur-sm"
          >
            <div
              data-testid="sidebar-logout-dialog-panel"
              data-surface="logout-dialog"
              className="w-full max-w-sm rounded-[var(--rely-radius-panel)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] p-5 text-[var(--rely-text-strong)] shadow-[var(--rely-shadow-panel)]"
            >
              <div
                data-testid="sidebar-logout-dialog-icon"
                data-tone="neutral"
                className="mb-4 flex h-11 w-11 items-center justify-center rounded-[var(--rely-radius-surface)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)] text-[var(--rely-text-muted)]"
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
              </div>
              <h3
                id="sidebar-logout-dialog-title"
                className="mb-2 text-lg font-semibold text-[var(--rely-text-strong)]"
              >
                {t('sidebar.logoutConfirmTitle')}
              </h3>
              <p
                id="sidebar-logout-dialog-description"
                className="mb-6 text-sm leading-6 text-[var(--rely-text-muted)]"
              >
                {t('sidebar.logoutConfirmMessage')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 rounded-[var(--rely-radius-control)] border border-[var(--rely-border-subtle)] px-4 py-2.5 text-sm font-medium text-[var(--rely-text-muted)] transition-colors hover:bg-[var(--rely-surface-elevated)] hover:text-[var(--rely-text-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 rounded-[var(--rely-radius-control)] border border-error-500/40 px-4 py-2.5 text-sm font-semibold text-error-600 transition-colors hover:bg-error-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error-500 dark:text-error-300"
                >
                  {t('sidebar.logout')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Sidebar;
